import React, { useEffect, useState } from "react";
import { Provider, useSelector } from "react-redux";
import { Router } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import Cookies from "./components/Cookies";
import DynamicManifest from "./components/DynamicManifest";
import GoogleTagManager from "./components/GoogleTagManager";
import Clarity from "./components/Clarity";
import PixelBus from "./components/PixelBus";
import AnalyticsRouteTracker from "./components/AnalyticsRouteTracker";
import HelmetCustom from "./components/HelmetCustom";
import JsonLd from "./components/JsonLd";
import LgpdAcceptModal from "./components/LgpdAcceptModal";
import Loading from "./components/Loading";
import OnInit from "./components/OnInit";
import ToastOffline from "./components/ToastOffline";
import { FilterProvider } from './contexts/FilterContext';
import { OrdenationProvider } from './contexts/OrdenationContext';
import useNavigatorOnLine from './hooks/useNavigatorOnline';
import Routes from "./routes";
import api from "./services/api";
import history from "./services/history";
import store from "./store";
import GlobalStyle from "./styles/global";
import associadas from "./styles/themes/associadas";
import msv from "./styles/themes/msv";
import { applyColor } from "./util/theme";
import { logoCabecalho } from "./util/logoLoja";
import { LoadingOverlayProvider } from "./contexts/LoadingOverlayContext";
import { AlertProvider } from "./contexts/AlertContext";

function App() {
  const isOnline = useNavigatorOnLine();
  const [user, setUser] = useState(null);
  const [lgpd, setLgpd] = useState(true);
  const signed = store.getState().auth.signed;
  const data = useSelector(state => state.store.data);
  const corPrimaria = useSelector(
    state => state.identidadeVisual?.data?.cores?.cor_primaria
  );
  const corFundo = useSelector(
    state => state.identidadeVisual?.data?.cores?.rodape_fundo
  );
  const identidade = useSelector(state => state.identidadeVisual?.data);
  const domainLoaded = useSelector(state => state.domain.allowedDomain);
  const domainError = useSelector(state => state.domain.error);
  // Segura o conteudo roteado ate store + identidade visual resolverem, para que
  // o primeiro paint ja venha com as cores do tenant. `|| domainError` evita
  // splash infinito caso `/stores/domain` falhe.
  const themeReady = domainLoaded || !!domainError;

  useEffect(() => {
    async function getUser() {
      if (signed) {
        const { data } = await api.get(`/customers/account`);
        setUser(data);
      }
    }

    getUser();
  }, []);

  const getThemeByUrl = (url) => {
    switch (url) {
      case msv.title:
        return msv;
      default:
        return associadas;
    }
  }

  return (
    <ThemeProvider theme={applyColor(getThemeByUrl(data?.empresa?.url), corPrimaria, corFundo)}>
      <OrdenationProvider>
        <FilterProvider>

          {!localStorage.getItem("@ConexaoGAM:Cookies") && (
            <Cookies />
          )}

          {(!user?.lgpd_aceite && user) && (
            <LgpdAcceptModal visible={lgpd} user={user} closeModal={() => setLgpd(false)} />
          )}

          <OnInit />
          <DynamicManifest />
          <GoogleTagManager />
          <Clarity />
          <PixelBus />
          <AnalyticsRouteTracker />
          <GlobalStyle />
          <HelmetCustom />
          {data?.empresa && (
            <>
              <JsonLd
                type="Store"
                data={{
                  nome: data.empresa.nome,
                  descricao_site: data.empresa.descricao_site,
                  siteUrl: window.location.origin,
                  logo: logoCabecalho(identidade, data.empresa) || '',
                  sac_telefone: data.empresa.sac_telefone,
                  sac_email: data.empresa.sac_email,
                }}
              />
              <JsonLd
                type="WebSite"
                data={{
                  nome: data.empresa.nome,
                  siteUrl: window.location.origin,
                  searchUrlTemplate: `${window.location.origin}/produtos/buscar/{search_term_string}`,
                }}
              />
            </>
          )}
          {!isOnline && (<ToastOffline />)}

          <LoadingOverlayProvider>
            <AlertProvider>
              {themeReady ? (
                <Router history={history}>
                  <Routes />
                </Router>
              ) : (
                <Loading />
              )}
            </AlertProvider>
          </LoadingOverlayProvider>
        </FilterProvider>
      </OrdenationProvider>
    </ThemeProvider>
  );
}

export default App;
